import { BreakpointType, ControlSheetBreakpoint, ControlSheetRow, ControlSheetTrip } from '../types/types'
import { isTimeSameOrAfter, isTimeBefore } from './breakpointTimeUtils'
import { standardizeTime } from './standardizeTime'

const tripHasMealRelief = (trip: ControlSheetTrip): boolean => {
    const breakpoints = trip.breakpoints
    for (let i = breakpoints.length - 1; i >= 0; i--) {
        const bp = breakpoints[i]
        if (bp === 'SKIP_CELL') {
            continue
        }
        if (isControlSheetBreakpoint(bp) && (bp.type === 'mealRelief' || bp.type === 'shiftEnd')) {
            return true
        } else {
            return false
        }
    }
    return false
}

const sortRuns = (a: ControlSheetRow, b: ControlSheetRow) => {
    const aOutbound = a.outbound
    const bOutbound = b.outbound
    const aInbound = a.inbound
    const bInbound = b.inbound

    if (aOutbound && bOutbound) {
        if (aOutbound.departStandTime === bOutbound.departStandTime) {
            const aMealRelief = tripHasMealRelief(aOutbound)
            const bMealRelief = tripHasMealRelief(bOutbound)
            if (aMealRelief && !bMealRelief) return -1
            if (!aMealRelief && bMealRelief) return 1
            // Both have meal relief (or neither) — break tie by drivingStartTime so that
            // the duty starting from the terminus sorts before one taking over mid-trip.
            if (
                aOutbound.drivingStartTime &&
                bOutbound.drivingStartTime &&
                aOutbound.drivingStartTime !== bOutbound.drivingStartTime
            ) {
                return isTimeBefore(aOutbound.drivingStartTime, bOutbound.drivingStartTime) ? -1 : 1
            }
            return 0
        } else {
            const before = isTimeBefore(aOutbound.departStandTime, bOutbound.departStandTime)
            return before ? -1 : 1
        }
    }

    // Mixed: one row has an outbound, the other doesn't. The row with the outbound
    // always sorts first within a run group — comparing by inbound departure time
    // here would be inconsistent (a later inbound trip could have an earlier time).
    if (aOutbound && !bOutbound) return -1
    if (!aOutbound && bOutbound) return 1

    if (aInbound && bInbound) {
        if (aInbound.departStandTime === bInbound.departStandTime) {
            if (tripHasMealRelief(aInbound)) {
                return -1
            } else if (tripHasMealRelief(bInbound)) {
                return 1
            } else if (
                aInbound.code === 'R' ||
                aInbound.code === 'RX' ||
                aInbound.code === 'F' ||
                aInbound.code === 'FX'
            ) {
                // R/RX/F/FX duty is being relieved or finishing — show it above the duty taking over
                return -1
            } else if (
                bInbound.code === 'R' ||
                bInbound.code === 'RX' ||
                bInbound.code === 'F' ||
                bInbound.code === 'FX'
            ) {
                return 1
            } else {
                return 0
            }
        } else {
            const before = isTimeBefore(aInbound.departStandTime, bInbound.departStandTime)
            return before ? -1 : 1
        }
    }

    // if (aOutbound && bInbound) {
    //     return isTimeBefore(aOutbound.departStandTime, bInbound.departStandTime) ? -1 : 1
    // }

    // if (aInbound && bOutbound) {
    //     return isTimeBefore(aInbound.departStandTime, bOutbound.departStandTime) ? -1 : 1
    // }

    return 0
}

function groupByRun(array: ControlSheetRow[]): ControlSheetRow[][] {
    if (!array || array.length === 0) {
        return []
    }

    const groups: ControlSheetRow[][] = []
    let currentGroup: ControlSheetRow[] = [array[0]]
    let currentKey: string = array[0].run

    for (let i = 1; i < array.length; i++) {
        const item = array[i]

        if (item.run === currentKey) {
            // Same key as current group, add to current group
            currentGroup.push(item)
        } else {
            // Different key, start new group
            groups.push(currentGroup)
            currentGroup = [item]
            currentKey = item.run
        }
    }

    // Don't forget to add the last group
    groups.push(currentGroup)

    return groups
}

const isControlSheetBreakpoint = (value: BreakpointType): value is ControlSheetBreakpoint => {
    return value !== 'SKIP_CELL' && value !== null && value.type !== 'empty'
}

const isTakeoverRow = (row: ControlSheetRow): boolean => {
    return !!(row.outbound?.isTakeoverRow || row.inbound?.isTakeoverRow)
}

/**
 * Ensures each row within a run has its outbound and inbound on the same duty.
 *
 * After initial pairing, a row's outbound and inbound may belong to different
 * duties. For each row where there's a mismatch, this function searches the
 * other rows in the same run for one whose outbound duty matches the current
 * row's inbound duty, then swaps the two outbounds.
 *
 * Example — before:
 *   Run      Outbound (duty)  Inbound (duty)
 *   A 051    124              123
 *   A 051    undefined        124
 *
 * After swap:
 *   Run      Outbound (duty)  Inbound (duty)
 *   A 051    124              124   ← outbounds swapped so duties align
 *   A 051    undefined        123
 *
 * Mutates the input array in place. Skips single-row runs (no swap needed).
 */

const matchupDuties = (run: ControlSheetRow[]) => {
    if (run.length === 1) {
        return
    }

    run.filter((r) => !isTakeoverRow(r)).forEach((r, index) => {
        const inbound = r.inbound
        const inboundDuty = r.inbound?.duty
        if (inbound && inboundDuty) {
            const outboundDuty = r.outbound?.duty

            const inboundDrivingStartTime = inbound.drivingStartTime

            const inboundTakingOver =
                inboundDrivingStartTime &&
                isTimeSameOrAfter(standardizeTime(inboundDrivingStartTime), inbound.departStandTime)

            if (inboundTakingOver && outboundDuty && outboundDuty !== inboundDuty) {
                // If the inbound duty is taking over from a DIFFERENT duty, that row should not
                // have an outbound duty, so switch with another row that has no outbound duty (if any).
                // We skip this when outbound/inbound duties already match — that means pairTripsProperly
                // produced the correct pairing and no swap is needed (e.g. duty 542_F starts driving
                // at the terminus so vStartTime == departStandTime, which would otherwise be
                // misidentified as a mid-trip takeover).
                for (const [index2, r2] of run.entries()) {
                    if (index2 === index) {
                        continue
                    }
                    if (!r2.outbound) {
                        const tempOutbound = r.outbound
                        r.outbound = r2.outbound
                        r2.outbound = tempOutbound
                        break
                    }
                }
            } else if (outboundDuty !== inboundDuty) {
                // Outbound/inbound duty mismatch — find another row in this run
                // whose outbound matches our inbound duty and swap outbounds
                for (const [index2, r2] of run.entries()) {
                    if (index2 === index || isTakeoverRow(r2)) {
                        continue
                    }
                    if (r2.outbound?.duty === inboundDuty) {
                        const tempOutbound = r.outbound
                        r.outbound = r2.outbound
                        r2.outbound = tempOutbound
                        break
                    }
                }
            }
        }
    })
}

// When two trips share a run and one has a meal relief/shift end, this function
// ensures the other trip's breakpoints before the meal relief start are set to 'empty',
// giving a cleaner visual layout on the control sheet.
const clearBreakpointsBeforeMealRelief = (trips: (ControlSheetTrip | undefined)[]) => {
    const tripIndexWithMealRelief = trips.findIndex((it) => it && tripHasMealRelief(it))
    if (tripIndexWithMealRelief === -1) {
        return
    }
    if (trips.length === 3) {
        // THIS IS A BACKEND BUG, DUPLICATED TRIPS
        return
    }

    const other = tripIndexWithMealRelief === 0 ? 1 : 0

    const tripWithMealRelief = trips[tripIndexWithMealRelief]
    const otherTrip = trips[other]

    if (!tripWithMealRelief || !otherTrip) {
        return
    }

    const mealReliefStartIndex = tripWithMealRelief.breakpoints.findIndex(
        (bp) => isControlSheetBreakpoint(bp) && (bp.type === 'mealRelief' || bp.type === 'shiftEnd')
    )

    const lastOtherBreakpoint = otherTrip.breakpoints[otherTrip.breakpoints.length - 1]

    if (isControlSheetBreakpoint(lastOtherBreakpoint)) {
        for (let i = otherTrip.breakpoints.length - 2; i >= 0; i--) {
            const bp = otherTrip.breakpoints[i]
            if (isControlSheetBreakpoint(bp)) {
                if (i >= mealReliefStartIndex - 1) {
                    bp.type = lastOtherBreakpoint.type
                } else {
                    bp.type = 'empty'
                }
            }
        }
    }
}

// Post-processes all control sheet rows after initial transformation and sorting.
// Steps:
// 1. Groups rows by run number
// 2. Clears breakpoints before meal relief on outbound/inbound trips for cleaner visuals
// 3. Matches up duties (swaps outbounds within a run so each row's outbound/inbound share the same duty)
// 4. Removes rows where both outbound and inbound are undefined
// 5. Re-sorts within each run by depart time (meal relief trips sorted first on tie)
// 6. Deduplicates rows with identical outbound+inbound trip number pairs
export const postprocessRun = (rows: ControlSheetRow[]) => {
    const grouped = groupByRun(rows)

    grouped.forEach((run) => {
        const nonTakeoverRows = run.filter((r) => !isTakeoverRow(r))
        const outbounds = nonTakeoverRows.map((item) => item.outbound).filter((item) => !!item)
        const inbounds = nonTakeoverRows.map((item) => item.inbound).filter((item) => !!item)

        if (outbounds && outbounds.length > 0) {
            clearBreakpointsBeforeMealRelief(outbounds)
        }
        if (inbounds && inbounds.length > 0) {
            clearBreakpointsBeforeMealRelief(inbounds)
        }

        // Ensure duties are matched correctly. I.e.
        // Run      Outbound  Inbound
        // A 051    124       123
        // A 051    undefined 124
        //
        // becomes
        // Run      Outbound  Inbound
        // A 051    124       124
        // A 051    undefined 123
        matchupDuties(run)
    })

    // Get rid of any rows that now have both outbound and inbound as undefined
    const emptyIndices: number[] = []
    rows.forEach((r, index) => {
        if (!r.outbound && !r.inbound) {
            emptyIndices.push(index)
        }
    })

    for (let i = emptyIndices.length - 1; i >= 0; i--) {
        rows.splice(emptyIndices[i], 1)
    }

    const runsToSwitch = [] as ControlSheetRow[][][]

    const grouped2 = groupByRun(rows)

    grouped2.forEach((run) => {
        const result = [...run].sort(sortRuns)
        if (result) {
            runsToSwitch.push([run, result])
        }
    })

    runsToSwitch.forEach(([run, result]) => {
        const rowIndices = run.map((r) => rows.indexOf(r))

        rowIndices.forEach((rowIndex, i) => {
            if (rowIndex !== -1) {
                rows[rowIndex] = result[i]
            }
        })
        // const firstIndex = rows.indexOf(pair[0])
        // const secondIndex = rows.indexOf(pair[1])
        // if (firstIndex !== -1 && secondIndex !== -1) {
        //     // Swap the two rows in the original array
        //     ;[rows[firstIndex], rows[secondIndex]] = [rows[secondIndex], rows[firstIndex]]
        // }
    })

    // // Remove duplicate trip pairs - trip numbers must be unique
    // const seenTrips = new Set<string>()
    // const duplicateIndices: number[] = []

    // rows.forEach((row, index) => {
    //     const outboundTrip = row.outbound?.trip ?? 'null'
    //     const inboundTrip = row.inbound?.trip ?? 'null'
    //     const tripKey = `${outboundTrip}-${inboundTrip}`

    //     if (seenTrips.has(tripKey)) {
    //         duplicateIndices.push(index)
    //     } else {
    //         seenTrips.add(tripKey)
    //     }
    // })

    // // Remove duplicates from end to start to preserve indices
    // for (let i = duplicateIndices.length - 1; i >= 0; i--) {
    //     rows.splice(duplicateIndices[i], 1)
    // }

    // // Further processing logic here
    // return Object.values(grouped).flat()
}
